import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { useLocalState } from '#hooks/local-state.ts';
import { useNavigationStore } from '#stores/navigation.ts';
import { extractSharedSearchParams, hasFilterChangesByType } from './utils.ts';

const skipFiltersDisclaimerSchema = z.boolean();

/**
 * Hook for handling the close functionality in drilldown components. Manages
 * navigation back to the previous route while preserving applicable search
 * parameters, while also showing a disclaimer dialog if there are changes in
 * the filters.
 */
export function useClose(options: {
  /**
   * Key used to identify the drilldown type. Used in the disclaimer dialog
   * logic and when comparing search params.
   */
  drilldownType: 'appointments' | 'forms';
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const navigationStore = useNavigationStore((state) => ({
    path: state.path,
    search: state.search,
  }));

  /**
   * Initial search params to compare against when closing the drilldown.
   * Either those coming from another route, or the initial search params from
   * the URL when accessing the drilldown route directly.
   */
  const [initialSearchParams] = React.useState(() =>
    navigationStore.path && navigationStore.search
      ? new URLSearchParams(navigationStore.search)
      : searchParams
  );

  const [skipFiltersDisclaimer, setSkipFiltersDisclaimer] = useLocalState({
    key: `skip-filters-disclaimer-${options.drilldownType}`,
    defaultValue: false,
    schema: skipFiltersDisclaimerSchema,
  });

  const [showFiltersDisclaimer, setShowFiltersDisclaimer] =
    React.useState(false);

  /** Main close handler to determine which action to take. */
  function handler(args: {
    doNotShowFiltersDisclaimerAgain: boolean;
    force: boolean;
  }) {
    if (args.doNotShowFiltersDisclaimerAgain) {
      setSkipFiltersDisclaimer(true);
    }

    if (
      !args.force &&
      !skipFiltersDisclaimer &&
      hasFilterChangesByType({
        initialSearchParams,
        activeSearchParams: searchParams,
        type: options.drilldownType,
      })
    ) {
      setShowFiltersDisclaimer(true);
      return;
    }

    void navigate({
      pathname: navigationStore.path || '/',
      search: extractSharedSearchParams(initialSearchParams).toString(),
    });
  }

  /**
   * Closes the filters disclaimer dialog without trying to close the main
   * dialog.
   */
  function dismissFiltersDisclaimer() {
    setShowFiltersDisclaimer(false);
  }

  return {
    dismissFiltersDisclaimer,
    handler,
    showFiltersDisclaimer,
  };
}
